/**
 * In-session store for campaign demo data (web).
 * Cleared when the tab closes (sessionStorage) or when demo mode is cleared.
 */

import { DEMO_USER_ID, isDemoMode } from './demoMode'

const STORE_KEY = 'tts_demo_store'

const emptyStore = () => ({
  appointments: [],
  notifications: [],
  supportRequests: [],
  favorites: {
    services: [],
    specialists: [],
    appointments: [],
  },
})

const readStore = () => {
  try {
    const raw = sessionStorage.getItem(STORE_KEY)
    if (!raw) return emptyStore()
    const parsed = JSON.parse(raw)
    return {
      appointments: Array.isArray(parsed.appointments) ? parsed.appointments : [],
      notifications: Array.isArray(parsed.notifications) ? parsed.notifications : [],
      supportRequests: Array.isArray(parsed.supportRequests) ? parsed.supportRequests : [],
      favorites: {
        services: Array.isArray(parsed.favorites?.services) ? parsed.favorites.services : [],
        specialists: Array.isArray(parsed.favorites?.specialists) ? parsed.favorites.specialists : [],
        appointments: Array.isArray(parsed.favorites?.appointments) ? parsed.favorites.appointments : [],
      },
    }
  } catch {
    return emptyStore()
  }
}

const writeStore = (store) => {
  try {
    sessionStorage.setItem(STORE_KEY, JSON.stringify(store))
  } catch (e) {
    console.warn('Demo store write failed:', e)
  }
}

export const clearDemoStore = () => {
  try {
    sessionStorage.removeItem(STORE_KEY)
  } catch {
    // ignore
  }
}

const makeId = () =>
  `demo-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`

export const demoAddAppointment = (appointmentData) => {
  const store = readStore()
  const id = makeId()
  const record = {
    id,
    user_id: appointmentData.userId || DEMO_USER_ID,
    profile_id: appointmentData.profileId || DEMO_USER_ID,
    professional_id: appointmentData.professionalId || null,
    salon_id: appointmentData.salonId || null,
    appointment_date: appointmentData.appointmentDate,
    start_time: appointmentData.startTime,
    appointment_time: appointmentData.startTime,
    end_time: null,
    duration_minutes: appointmentData.durationMinutes,
    services: appointmentData.services || [],
    professional_data: appointmentData.professionalData || null,
    professional: appointmentData.professionalData?.primary || null,
    total_amount: appointmentData.totalAmount,
    payment_method: appointmentData.paymentMethod || 'cash',
    payment_status: appointmentData.paymentStatus || 'unpaid',
    status: appointmentData.status || 'confirmed',
    notes: appointmentData.notes || '[DEMO — not saved to database]',
    created_at: new Date().toISOString(),
    _demo: true,
  }
  store.appointments.unshift(record)
  writeStore(store)
  return { success: true, data: record }
}

export const demoGetAppointments = (userId) => {
  const store = readStore()
  const uid = userId || DEMO_USER_ID
  return store.appointments.filter((a) => !userId || a.user_id === uid || a.user_id === DEMO_USER_ID)
}

export const demoCancelAppointment = (appointmentId) => {
  const store = readStore()
  const idx = store.appointments.findIndex((a) => a.id === appointmentId)
  if (idx === -1) return { success: false, error: 'Appointment not found' }
  store.appointments[idx] = {
    ...store.appointments[idx],
    status: 'cancelled',
  }
  writeStore(store)
  return { success: true, data: store.appointments[idx] }
}

export const demoUpdateAppointment = (appointmentId, patch) => {
  const store = readStore()
  const idx = store.appointments.findIndex((a) => a.id === appointmentId)
  if (idx === -1) return { success: false, error: 'Appointment not found' }
  store.appointments[idx] = { ...store.appointments[idx], ...patch }
  writeStore(store)
  return { success: true, data: store.appointments[idx] }
}

export const demoAddFavoriteService = (serviceId, professionalId = null) => {
  const store = readStore()
  if (!store.favorites.services.includes(serviceId)) {
    store.favorites.services.push(serviceId)
  }
  writeStore(store)
  return { success: true, error: null }
}

export const demoRemoveFavoriteService = (serviceId) => {
  const store = readStore()
  store.favorites.services = store.favorites.services.filter((id) => id !== serviceId)
  writeStore(store)
  return { success: true, error: null }
}

export const demoAddFavoriteProfessional = (professionalId) => {
  const store = readStore()
  if (!store.favorites.specialists.includes(professionalId)) {
    store.favorites.specialists.push(professionalId)
  }
  writeStore(store)
  return { success: true, error: null }
}

export const demoRemoveFavoriteProfessional = (professionalId) => {
  const store = readStore()
  store.favorites.specialists = store.favorites.specialists.filter((id) => id !== professionalId)
  writeStore(store)
  return { success: true, error: null }
}

export const demoGetFavoriteServiceIds = () => readStore().favorites.services
export const demoGetFavoriteProfessionalIds = () => readStore().favorites.specialists

export const demoGetAllFavorites = () => {
  const f = readStore().favorites
  return {
    success: true,
    data: {
      services: f.services,
      professionals: f.specialists,
      appointments: f.appointments,
    },
    error: null,
  }
}

export const demoAddFavoriteAppointment = (appointmentData) => {
  const store = readStore()
  const hash = appointmentData?.hash || makeId()
  store.favorites.appointments.push({ ...appointmentData, hash, _demo: true })
  writeStore(store)
  return { success: true, error: null }
}

export const demoRemoveFavoriteAppointment = (hash) => {
  const store = readStore()
  store.favorites.appointments = store.favorites.appointments.filter((a) => a.hash !== hash)
  writeStore(store)
  return { success: true, error: null }
}

export const demoGetFavoriteAppointments = () => readStore().favorites.appointments

export const demoAddNotification = ({ title, message, type, relatedId = null }) => {
  const store = readStore()
  const record = {
    id: makeId(),
    user_id: DEMO_USER_ID,
    title,
    body: message,
    message,
    data: { type },
    type,
    related_id: relatedId,
    read: false,
    created_at: new Date().toISOString(),
    _demo: true,
  }
  store.notifications.unshift(record)
  writeStore(store)
  return record
}

export const demoGetNotifications = (limit = 50, unreadOnly = false) => {
  const list = readStore().notifications
  return (unreadOnly ? list.filter((n) => !n.read) : list).slice(0, limit)
}

export const demoGetUnreadNotificationCount = () =>
  readStore().notifications.filter((n) => !n.read).length

export const demoMarkNotificationRead = (notificationId) => {
  const store = readStore()
  const idx = store.notifications.findIndex((n) => n.id === notificationId)
  if (idx !== -1) {
    store.notifications[idx] = { ...store.notifications[idx], read: true }
    writeStore(store)
  }
  return { success: true }
}

export const demoMarkAllNotificationsRead = () => {
  const store = readStore()
  store.notifications = store.notifications.map((n) => ({ ...n, read: true }))
  writeStore(store)
  return { success: true }
}

export const demoAddSupportRequest = (requestData) => {
  const store = readStore()
  const record = {
    id: makeId(),
    user_id: DEMO_USER_ID,
    first_name: requestData.firstName || '',
    last_name: requestData.lastName || '',
    email: requestData.email || '',
    phone: requestData.phone || '',
    note: requestData.note || '',
    status: 'pending',
    created_at: new Date().toISOString(),
    _demo: true,
  }
  store.supportRequests.unshift(record)
  writeStore(store)
  return record
}

export const demoGetSupportRequests = () => readStore().supportRequests

/** Guard helper for services */
export const assertDemoOrContinue = () => isDemoMode()
